/* eslint-disable */ import { DomainBuilder, DomainComponentOpticBuilder, InvariantBuilder, RecipeFactoryBuilder, createConstantExpressionFactory, createCustomExpressionFactory, createCustomInvariantFactory, createRelationFactory } from '@salsita-ai/platform-project-sdk/domain';
import { BackendData, FrontendData, PublishedData, SessionData } from './application-types.js';
import { $entityReflections, constants } from './enhanced-model.js';
import { AnyDomainEntity, RootEntity, aliases, entities, modelVersion } from './model/index.js';
const implementRelation = createRelationFactory(aliases);
export const relationExpressions = {
    Plate: {
        $allAnchorPoints: implementRelation({
            kind: 'allOfType',
            target: 'AnchorPoint',
            cardinality: '0-N'
        }),
        $customBend: implementRelation({
            kind: 'allOfType',
            target: 'CustomBend',
            cardinality: '0-1'
        }),
        $holes: implementRelation({
            kind: 'allOfType',
            target: 'Hole',
            cardinality: '0-N'
        }),
        $powderCoatingMountingHole: implementRelation({
            kind: 'direct',
            target: 'FixingHole',
            field: 'powderCoatingMountingHoleId',
            cardinality: '0-1'
        })
    },
    AnchorableShape: {
        $anchorPoints: implementRelation({
            kind: 'indirect',
            target: 'AnchorPoint',
            field: 'shapeId',
            cardinality: '0-N'
        })
    },
    StrikePlate: {
        $tongueBend: implementRelation({
            kind: 'allOfType',
            target: 'StrikeBend',
            cardinality: '0-1'
        })
    },
    KickPlate: {
        $bulkOrderItems: implementRelation({
            kind: 'allOfType',
            target: 'KickPlateBulkOrderItem',
            cardinality: '0-N'
        })
    },
    Hole: {
        $plate: implementRelation({
            kind: 'allOfType',
            target: 'Plate',
            cardinality: '1-1'
        }),
        $anchorPointFrom: implementRelation({
            kind: 'direct',
            target: 'AnchorPoint',
            field: 'anchorPointFromId',
            cardinality: '1-1'
        }),
        $anchorPointTo: implementRelation({
            kind: 'direct',
            target: 'AnchorPoint',
            field: 'anchorPointToId',
            cardinality: '1-1'
        })
    },
    AnchorPoint: {
        $shape: implementRelation({
            kind: 'direct',
            target: 'AnchorableShape',
            field: 'shapeId',
            cardinality: '1-1'
        }),
        $plate: implementRelation({
            kind: 'allOfType',
            target: 'Plate',
            cardinality: '1-1'
        })
    },
    KickPlateBulkOrderItem: {
        $plate: implementRelation({
            kind: 'allOfType',
            target: 'KickPlate',
            cardinality: '1-1'
        })
    },
    CustomBend: {
        $plate: implementRelation({
            kind: 'allOfType',
            target: 'Plate',
            cardinality: '1-1'
        })
    },
    StrikeBend: {
        $plate: implementRelation({
            kind: 'allOfType',
            target: 'StrikePlate',
            cardinality: '1-1'
        })
    }
};
export const constantExpressions = createConstantExpressionFactory({} // insert expression metas here, once ready
)(constants);
export const createRecipe = RecipeFactoryBuilder.forAliases(aliases).forEntities(AnyDomainEntity.reflections).withDefaults({}).build();
const staticViolations = {
    Hole: {
        closeToEdge: {
            type: 'closeToEdge',
            severity: 'fatal',
            message: 'The hole can not be too close to the edge of the plate'
        },
        closeToCustomBend: {
            type: 'closeToCustomBend',
            severity: 'fatal',
            message: 'The hole can not be too close to the bend'
        },
        closeToStrikeBend: {
            type: 'closeToStrikeBend',
            severity: 'fatal',
            message: 'The hole can not be too close to the bend'
        },
        kickPlateFixingHolesMissing: {
            type: 'kickPlateFixingHolesMissing',
            severity: 'fatal',
            message: 'Fixing holes must be enabled at least in one direction'
        }
    },
    KickPlateBulkOrderItem: {
        kickPlateFixingHolesMissing: {
            type: 'kickPlateFixingHolesMissing',
            severity: 'fatal',
            message: 'Fixing holes must be enabled at least in one direction.'
        }
    },
    Plate: {
        materialNotAvailable: {
            type: 'materialNotAvailable',
            severity: 'fatal',
            message: 'This material is not available for the selected configuration'
        },
        rawMaterialNotAvailable: {
            type: 'rawMaterialNotAvailable',
            severity: 'fatal',
            message: 'This material is not available for the selected finish'
        },
        finishNotAvailable: {
            type: 'finishNotAvailable',
            severity: 'fatal',
            message: 'This finish is not available for the selected material'
        },
        thicknessNotAvailable: {
            type: 'thicknessNotAvailable',
            severity: 'fatal',
            message: 'This thickness is not available for the selected material'
        },
        countersinkNotAvailable: {
            type: 'countersinkNotAvailable',
            severity: 'fatal',
            message: 'This countersink is not available for the selected thickness'
        },
        powderCoatingAvailable: {
            type: 'powderCoatingAvailable',
            severity: 'fatal',
            message: 'Powder coating is available only for stainless steel and aluminum plates.'
        },
        powderCoatingAvailableForMaterial: {
            type: 'powderCoatingAvailableForMaterial',
            severity: 'fatal',
            message: 'Powder coating is not available for the selected material.'
        },
        hasBendTool: {
            type: 'hasBendTool',
            severity: 'fatal',
            message: 'Bending not available for this thickness'
        },
        bendNotTooLong: {
            type: 'bendNotTooLong',
            severity: 'fatal',
            message: 'The length in bend direction is too large for the selected bend type'
        },
        strikePlateWidthSmallerThanTongueWidth: {
            type: 'strikePlateWidthSmallerThanTongueWidth',
            severity: 'fatal',
            message: "Plate's tongue is too large"
        }
    }
};
export const implementCustomInvariants = createCustomInvariantFactory()(staticViolations);
export const invariantBuilder = new InvariantBuilder(aliases);
export const attachComponent = DomainComponentOpticBuilder.from(aliases);
export const implementCustomExpressions = createCustomExpressionFactory({} // TODO pass in meta-data once we have them
);
export const domainBuilder = DomainBuilder.from(entities, aliases, $entityReflections, RootEntity.types, modelVersion, PublishedData, SessionData, FrontendData, BackendData, false).withExpressions(constantExpressions).withExpressions(relationExpressions);

//# sourceMappingURL=implementation.js.map